import { useCallback } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useFeature } from '~/modules/root';
import type { PinItemType } from '~/modules/moodboards/type';
import { useHandleFollow } from '../hook/use-handle-follow';
import type { SaveGateIntent } from '../save-gate-state';
import { SaveGateModal } from './SaveGateModal';

/**
 * Site-wide host for the save gate, mounted once behind `revamp_2026`.
 * The flag check stands alone so a flag-off page pays for nothing — the data
 * hooks (and the aria display-mode context they require) only run inside.
 */
export const SaveGateHost = () => {
    const enabled = useFeature('revamp_2026');
    if (!enabled) return null;
    return <SaveGateHostInner />;
};

/**
 * Resumes a save by writing the pin into the board picked in the gate. The
 * gate's own success step reports the outcome, so the popover path's success
 * toast stays off; failures still surface through the shared error toast.
 */
const SaveGateHostInner = () => {
    const tracker = useTracker();
    const { handleSaveToBoard } = useHandleFollow();

    const handleResume = useCallback(
        async (intent: SaveGateIntent): Promise<boolean> => {
            const { board } = intent;
            if (
                intent.action !== 'save' ||
                !board ||
                !intent.pinId ||
                !intent.pinType
            ) {
                // Nothing opens the gate with a follow intent yet, and a save
                // cannot land without a destination.
                return false;
            }

            const items = intent.items?.length
                ? intent.items
                : [{ id: intent.pinId, libraryItemId: intent.libraryItemId }];
            const landed = await handleSaveToBoard(
                {
                    id: board.id,
                    title: board.title,
                    urlSegment: board.urlSegment,
                    projectUrlSegment: board.projectUrlSegment,
                    isPersonal: board.isPersonal,
                    entityType: 'moodboard',
                    thumbnailUrls: [],
                },
                items,
                intent.pinType as PinItemType,
                { showSuccessToast: false },
            );

            if (landed) {
                const itemId = items[0]?.libraryItemId ?? items[0]?.id;
                if (itemId) {
                    void tracker.log('SaveSubmit', {
                        url: new URL(window.location.href),
                        data: { LibraryItemID: itemId },
                    });
                }
            }
            return landed;
        },
        [handleSaveToBoard, tracker],
    );

    return <SaveGateModal onResume={handleResume} />;
};
